import { Fragment, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Pill,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Link, useLocation, useSearchParams } from 'react-router';
import {
  LABEL_PARAMS,
  filtersFromSearch,
  useFeedback,
  withFilters,
  type AdminFeedbackRow,
  type FeedbackParam,
} from '../hooks/useFeedback';
import { FEEDBACK_LABELS, SUBSCRIPTION_STATUSES, type ClientMetaKey } from '../lib/api-enums';

const COLUMNS = 9;
const SENTIMENT_COLOR: Record<string, string> = {
  negative: 'red',
  neutral: 'gray',
  positive: 'green',
};
const words = (value: string) => value.replace(/_/g, ' ');
const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: words(value) }));
const LABEL_SELECT: Record<(typeof LABEL_PARAMS)[number], { label: string; width: number }> = {
  kind: { label: 'Kind', width: 170 },
  surface: { label: 'Surface', width: 170 },
  sentiment: { label: 'Sentiment', width: 150 },
};

/** Labels for the expanded row, in display order. A Record, so a new client field needs a label. */
const CLIENT_META_LABELS: Record<ClientMetaKey, string> = {
  os_version: 'OS version',
  device_model: 'Device',
  locale: 'Locale',
  update_id: 'OTA update',
  runtime_version: 'Runtime',
  git_sha: 'Commit',
};

function LabelBadge({ value, color = 'gray' }: { value: string | null; color?: string }) {
  return value ? (
    <Badge size="sm" variant="light" color={color}>
      {words(value)}
    </Badge>
  ) : (
    <Text c="dimmed">—</Text>
  );
}

function Detail({ row }: { row: AdminFeedbackRow }) {
  const meta = (Object.entries(CLIENT_META_LABELS) as [ClientMetaKey, string][]).filter(
    ([key]) => row.client_meta[key],
  );
  return (
    <Stack gap="xs" py="xs">
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {row.body}
      </Text>
      {row.voice_used && (
        <Badge size="xs" variant="light" w="fit-content">
          Dictated
        </Badge>
      )}
      {row.classified_at && (
        <Text size="xs" c="dimmed">
          Sentiment score {row.sentiment_score ?? '—'} · classified{' '}
          {new Date(row.classified_at).toLocaleString()} ·{' '}
          {row.classifier_prompt_version ?? 'unknown prompt'}
        </Text>
      )}
      {meta.length > 0 && (
        <Table withRowBorders={false} verticalSpacing={2} w="fit-content">
          <Table.Tbody>
            {meta.map(([key, label]) => (
              <Table.Tr key={key}>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {label}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">
                    {row.client_meta[key]}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

/** Mantine DatePickerInput's range: 'YYYY-MM-DD' strings, sent to the API as UTC days. */
type Range = [string | null, string | null];

/**
 * The picker emits [from, null] after the first click. Every filter change is a fetch and an
 * audit row, so the half-picked range stays here and reaches onCommit only once both ends are
 * set, or both are cleared. allowSingleDateInRange makes a second click on the same day emit
 * [day, day], one UTC day, instead of clearing the range.
 */
function SentRangePicker({ value, onCommit }: { value: Range; onCommit: (range: Range) => void }) {
  const [picked, setPicked] = useState<Range>(value);
  return (
    <DatePickerInput
      type="range"
      allowSingleDateInRange
      label="Sent"
      description="UTC days"
      placeholder="Any date"
      value={picked}
      onChange={(range) => {
        setPicked(range);
        if ((range[0] === null) === (range[1] === null)) onCommit(range);
      }}
      clearable
      w={240}
    />
  );
}

/** The state UserDetail's Feedback link carries. */
export interface LinkedMember {
  memberId: string;
  memberName: string;
}

export function Feedback() {
  // Every filter lives in the URL, so a link can open the screen filtered (the user page
  // links to /feedback?user_id=…) and Back returns to the filtered list. An edit here
  // replaces the history entry: Back leaves the screen rather than undoing one filter.
  const [searchParams, setSearchParams] = useSearchParams();
  // The user page's link carries the member's name, so the chip can name them before any of
  // their feedback loads. A filter change passes the state on, or the name would be lost.
  const location = useLocation();
  const linked = location.state as LinkedMember | null;
  const filters = filtersFromSearch(searchParams);
  const set = (patch: Partial<Record<FeedbackParam, string | null>>) =>
    setSearchParams((prev) => new URLSearchParams(withFilters(filtersFromSearch(prev), patch)), {
      replace: true,
      state: location.state,
    });
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useFeedback(filters);
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const { user_id: userId } = filters;
  const unclassified = filters.unclassified === 'true';
  const member = userId ? rows.find((r) => r.user_id === userId.toLowerCase()) : undefined;
  const linkedName =
    userId && linked?.memberId?.toLowerCase() === userId.toLowerCase()
      ? linked.memberName
      : undefined;

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        What members sent from Send feedback, newest first. Every view of this page is recorded in
        the audit log.
      </Text>

      {userId && (
        <Group>
          <Pill
            size="md"
            withRemoveButton
            onRemove={() => set({ user_id: null })}
            // Pill hides its remove button from assistive tech by default; here it is the only
            // way to clear the filter, so it gets a name and a tab stop.
            removeButtonProps={{
              'aria-label': 'Clear member filter',
              'aria-hidden': false,
              tabIndex: 0,
            }}
          >
            {`Member: ${member?.display_name ?? member?.email ?? linkedName ?? userId}`}
          </Pill>
        </Group>
      )}

      <Group align="end">
        {LABEL_PARAMS.map((param) => (
          <Select
            key={param}
            label={LABEL_SELECT[param].label}
            placeholder="Any"
            clearable
            data={options(FEEDBACK_LABELS[param])}
            value={filters[param] ?? null}
            onChange={(value) => set({ [param]: value })}
            disabled={unclassified}
            w={LABEL_SELECT[param].width}
          />
        ))}
        <Select
          label="Subscription status"
          placeholder="Any"
          clearable
          data={options(SUBSCRIPTION_STATUSES)}
          value={filters.subscription_status ?? null}
          onChange={(status) => set({ subscription_status: status })}
          w={190}
        />
        {/* Keyed on the URL's range: a change underneath, as when the Feedback nav link clears
            every filter, remounts the picker on the new range. */}
        <SentRangePicker
          key={`${filters.from ?? ''}|${filters.to ?? ''}`}
          value={[filters.from ?? null, filters.to ?? null]}
          onCommit={([from, to]) => set({ from, to })}
        />
        <Switch
          label="Not yet classified"
          checked={unclassified}
          // Turning it on drops the label filters: unclassified rows have none to match.
          onChange={(e) => set({ unclassified: e.currentTarget.checked ? 'true' : null })}
        />
      </Group>

      {query.isError && (
        <Alert color="red" title="Couldn't load feedback">
          <Group>
            <Text size="sm">{query.error.message}</Text>
            <Button size="xs" variant="light" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </Group>
        </Alert>
      )}

      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>When</Table.Th>
            <Table.Th>Member</Table.Th>
            <Table.Th>Feedback</Table.Th>
            <Table.Th>Kind</Table.Th>
            <Table.Th>Surface</Table.Th>
            <Table.Th>Sentiment</Table.Th>
            <Table.Th>Actionability</Table.Th>
            <Table.Th>App</Table.Th>
            <Table.Th>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Fragment key={row.id}>
              <Table.Tr>
                <Table.Td>
                  <Text size="sm">{new Date(row.created_at).toLocaleString()}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Anchor component={Link} to={`/users/${row.user_id}`} size="sm">
                      {row.display_name ?? row.email ?? 'Unknown member'}
                    </Anchor>
                    {row.is_sim && (
                      <Badge size="xs" color="grape" variant="light">
                        sim
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td maw={360}>
                  {/* A button, so the row opens from the keyboard as well as by click. */}
                  <UnstyledButton
                    ta="left"
                    aria-expanded={openId === row.id}
                    aria-controls={`feedback-detail-${row.id}`}
                    onClick={() => setOpenId(openId === row.id ? null : row.id)}
                  >
                    <Text size="sm" lineClamp={2}>
                      {row.body}
                    </Text>
                  </UnstyledButton>
                </Table.Td>
                {row.classified_at ? (
                  <>
                    <Table.Td>
                      <LabelBadge value={row.kind} />
                    </Table.Td>
                    <Table.Td>
                      <LabelBadge value={row.surface} />
                    </Table.Td>
                    <Table.Td>
                      <LabelBadge
                        value={row.sentiment}
                        color={SENTIMENT_COLOR[row.sentiment ?? ''] ?? 'gray'}
                      />
                    </Table.Td>
                    <Table.Td>
                      <LabelBadge value={row.actionability} />
                    </Table.Td>
                  </>
                ) : (
                  <Table.Td colSpan={4}>
                    <Badge size="sm" variant="outline" color="gray">
                      unclassified
                    </Badge>
                  </Table.Td>
                )}
                <Table.Td>
                  <Text size="sm">{`${row.platform} ${row.app_version ?? '?'} (${row.build_number ?? '?'})`}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{words(row.subscription_status)}</Text>
                </Table.Td>
              </Table.Tr>
              {openId === row.id && (
                <Table.Tr id={`feedback-detail-${row.id}`}>
                  <Table.Td colSpan={COLUMNS}>
                    <Detail row={row} />
                  </Table.Td>
                </Table.Tr>
              )}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>

      {query.isFetching && <Loader size="sm" />}
      {!query.isFetching && !query.isError && rows.length === 0 && (
        <Text c="dimmed" size="sm">
          {Object.keys(filters).length > 0
            ? 'No feedback matches these filters.'
            : 'No feedback yet.'}
        </Text>
      )}
      {query.hasNextPage && (
        <Button variant="light" onClick={() => void query.fetchNextPage()}>
          Load more
        </Button>
      )}
    </Stack>
  );
}
